/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { ROUTES } from '@sportninja/common/constants/app'
import { t } from '@sportninja/common/i18n'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Avatar,
  Table,
  Input,
  Spin,
  Select,
  Tooltip,
  Alert,
  AutoComplete,
  TreeSelect,
  FloatButton,
} from 'antd'
import { paragraphSmall, weightRegular } from '../../components/css'
import {
  SearchOutlined,
  ArrowUpOutlined,
  DownOutlined,
} from '@ant-design/icons'
import OptionButtonIcon from 'src/components/List/OptionButtons/Icon'
import OptionButtonDelete from '../../components/List/OptionButtons/Delete'
import MoreOptions, {
  MoreOptionListItem,
} from '../../components/List/OptionButtons/More'
import useTeamList from './useTeamList'
import colors from '@sportninja/common/constants/appColors'
import Icon from '../../components/Icon'
import useWindowSize from 'src/hooks/useWindowSize'
import { useSports } from '@sportninja/common/hooks/useSports'
import { getAllGenericSportProperties } from '@sportninja/common/selectors/types'
import { connect } from 'react-redux'
import req from '@sportninja/common/api/request'
import { getTableStyles } from 'src/styles/tableStyles'
import TeamModal from 'src/components/TeamModal'
import parsers from '@sportninja/common/reducers/parsers'
import Text from 'src/components/ts-components/Text'
import MembershipTypeFilter from 'src/pages/MySchedule/components/MembershipTypeFilter'
import MembershipTypeInfoLabel from 'src/pages/MySchedule/components/MembershipTypeFilter/MembershipTypeInfoLabel'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import {
  transformToTreeSelectData,
  findNodeById,
  treeNodeToOrganization,
  getFirstLevelKeys,
} from '../MySchedule/utils/treeSelectUtils'

const SCROLL_THRESHOLD = 500
const ORGANIZATIONS_SCROLL_THRESHOLD = 50
const DEBOUNCE_MS = 500
const ALL_VALUE = 'all'
const ORG_PLACEHOLDER =
  '/images/generic_placeholders/placeholder_organization.png'
const COMPETITION_PLACEHOLDER =
  '/images/generic_placeholders/placeholder_competition.png'

const filterAutoCompleteStyle = css`
  ${paragraphSmall}
  ${weightRegular}
  width: 200px;
  height: 40px !important;
  color: ${colors.WHITE};

  .ant-select-selector {
    background: transparent !important;
    border-radius: 8px !important;
    height: 40px !important;
  }

  .ant-select-selection-search {
    display: flex;
    align-items: center;
  }

  .ant-select-selection-search-input {
    height: 38px !important;
    color: ${colors.WHITE} !important;
    text-overflow: ellipsis !important;
    overflow: hidden !important;
    white-space: nowrap !important;
  }

  .ant-select-selection-placeholder {
    line-height: 38px !important;
    font-size: 14px !important;
    color: ${colors.NEUTRAL_50} !important;
  }

  .ant-select-clear {
    color: ${colors.WHITE};
  }

  @media (max-width: 1023px) {
    width: 100%;
  }
`

const filterTreeSelect = css`
  ${paragraphSmall}
  ${weightRegular}
  width: 200px;
  height: 40px !important;
  color: ${colors.WHITE};

  .ant-select-selector {
    background: transparent !important;
    border-radius: 8px !important;
    height: 40px !important;
  }

  .ant-select-selection-search-input {
    height: 38px !important;
    color: ${colors.WHITE} !important;
  }

  .ant-select-selection-item {
    font-size: 14px !important;
    line-height: 38px !important;
    color: ${colors.WHITE} !important;
  }

  .ant-select-selection-placeholder {
    line-height: 38px !important;
    font-size: 14px !important;
    color: ${colors.NEUTRAL_50} !important;
  }

  .ant-select-clear {
    color: ${colors.WHITE};
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`

const treeNodeStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  min-height: 36px;
`

const treeNodeImageStyle = css`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid ${colors.NEUTRAL_50};
  align-self: center;
`

const treeNodeTextStyle = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  flex: 1;
`

const treeNodeNameStyle = css`
  ${paragraphSmall}
  ${weightRegular}
  color: ${colors.WHITE};
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const optionImageStyle = css`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid ${colors.NEUTRAL_50};
`

const CustomOptionButtonFavourite = ({
  entityType,
  item,
  isFavourite,
  isFavoritesView,
  onFavoriteChange,
}) => {
  const [loading, setLoading] = useState(false)
  const [isFav, setIsFav] = useState(isFavourite)

  useEffect(() => {
    setIsFav(isFavourite)
  }, [isFavourite])

  const entityName = useCallback(() => {
    switch (entityType) {
      case ENTITY_TYPES.org:
        return ' organization'
      case ENTITY_TYPES.team:
        return ' team'
      case ENTITY_TYPES.schedule:
        return ' competition'
      case ENTITY_TYPES.game:
        return ' game'
      default:
        return ''
    }
  }, [entityType])

  const onClick = async () => {
    setLoading(true)
    try {
      if (isFavoritesView) {
        const newState = !isFav
        setIsFav(newState)
        onFavoriteChange(newState)
      } else {
        await req(`/favorites/${entityType}/${item.id}`, {
          method: isFav ? 'DELETE' : 'POST',
        })
        setIsFav(!isFav)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        justify-content: center;
      `}
    >
      {loading ? (
        <div style={{ width: 18 }}>
          <Spin size='small' style={{ color: colors.PRIMARY }} />
        </div>
      ) : (
        <div
          css={css`
            position: relative;
            display: inline-block;
          `}
        >
          <Tooltip
            title={`${isFav ? 'Unfollow' : 'Follow'}${entityName()}`}
            mouseEnterDelay={1}
            placement='bottom'
          >
            <OptionButtonIcon
              faType={isFav ? 'fas' : 'far'}
              name='star'
              onClick={onClick}
            />
          </Tooltip>
        </div>
      )}
    </div>
  )
}

const renderGeneratePluginTag = ({ onClick }) => {
  return (
    <MoreOptionListItem
      onClick={() => {
        onClick()
      }}
      iconName='plug'
      faType='far'
      title={'Generate JS Plugin Tag'}
    />
  )
}

const TeamListTable = ({
  refreshKey,
  setCount = () => {},
  slug,
  genericSports,
  onClickWebPluginOnTeamList = () => {},
  isFavorites = false,
  parentId,
  isMembership = false,
  initialMembershipType = 'both',
  shouldShowUsersMeFilters = false,
  specificNameColumn = 'name',
  onMembershipTypeChange = null,
  queryRecursive = false,
  displayOrganizationColumn = false,
  showTotalCount = false,
  externalMembershipType,
  onMembershipTypeChangeExternal,
}) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width < 1024, [width])
  const { sports, fetchSports } = useSports()

  const {
    items,
    loading,
    hasMore,
    totalCount,
    permissions,
    search,
    sportFilter,
    handleSearch,
    handleSort,
    handleSportFilter,
    handleOrganizationIdChange,
    handleScheduleIdChange,
    loadMore,
    refresh,
    sort,
    direction,
    updateSportFilterList,
    error,
    membershipType,
    handleMembershipType,
  } = useTeamList(slug, {
    initialSort: specificNameColumn,
    initialMembershipType,
    queryRecursive,
    isMembership,
    externalMembershipType,
    onMembershipTypeChangeExternal,
  })

  // Local state for managing favorites when isFavorites is true
  const [localFavorites, setLocalFavorites] = useState(new Set())

  // State for TeamModal
  const [editingTeam, setEditingTeam] = useState(null)

  // Organizations filter state (only used when shouldShowUsersMeFilters is true)
  const [organizationsTree, setOrganizationsTree] = useState(null)
  const [organizationsLoading, setOrganizationsLoading] = useState(false)
  const [selectedOrganization, setSelectedOrganization] = useState(null)
  const [organizationsDropdownOpen, setOrganizationsDropdownOpen] =
    useState(false)

  // Schedules filter state (only used when shouldShowUsersMeFilters is true)
  const [schedules, setSchedules] = useState([])
  const [schedulesLoading, setSchedulesLoading] = useState(false)
  const [schedulesAppending, setSchedulesAppending] = useState(false)
  const [schedulesPagination, setSchedulesPagination] = useState(null)
  const [selectedSchedule, setSelectedSchedule] = useState(null)
  const [schedulesDropdownOpen, setSchedulesDropdownOpen] = useState(false)
  const schedulesSearchTimeoutRef = useRef(null)
  const schedulesSearchQueryRef = useRef('')

  // Ref to prevent multiple simultaneous loadMore calls
  const isLoadingMoreRef = useRef(false)

  // Ref to track initial mount and prevent refresh on first render
  const isInitialMountRef = useRef(true)

  useEffect(() => {
    if (!sports || sports?.length === 0) {
      fetchSports()
    }
  }, [fetchSports, sports])

  // Fetch organizations tree for filter (only when shouldShowUsersMeFilters is true)
  const fetchOrganizationsTree = useCallback(async () => {
    if (!shouldShowUsersMeFilters) {
      return
    }

    setOrganizationsLoading(true)
    try {
      const query = { membership_type: membershipType }
      const { data } = await req('/users/me2/organizations/tree', { query })
      setOrganizationsTree(data || [])
    } catch (error) {
      console.error('Error fetching organizations tree:', error)
      setOrganizationsTree([])
    } finally {
      setOrganizationsLoading(false)
    }
  }, [shouldShowUsersMeFilters, membershipType])

  // Fetch schedules for filter (only when shouldShowUsersMeFilters is true)
  const fetchSchedules = useCallback(
    async (searchQuery = '', page = 1, append = false) => {
      if (!shouldShowUsersMeFilters) {
        return
      }

      if (append) {
        setSchedulesAppending(true)
      } else {
        setSchedulesLoading(true)
      }

      try {
        const query = {
          page,
          sort: 'name',
          direction: 'asc',
          membership_type: membershipType,
          ...(searchQuery && { query: searchQuery }),
        }
        if (queryRecursive) {
          query.recursive = 1
        }
        const { data, meta } = await req('/users/me2/schedules', { query })

        if (append) {
          setSchedules((prev) => [...prev, ...data])
        } else {
          setSchedules(data)
        }
        setSchedulesPagination(meta?.pagination || null)
      } finally {
        setSchedulesLoading(false)
        setSchedulesAppending(false)
      }
    },
    [shouldShowUsersMeFilters, membershipType]
  )

  // Initial fetch of organizations tree
  useEffect(() => {
    if (shouldShowUsersMeFilters) {
      fetchOrganizationsTree()
    }
  }, [shouldShowUsersMeFilters, fetchOrganizationsTree])

  // Initial fetch of schedules
  useEffect(() => {
    if (shouldShowUsersMeFilters) {
      fetchSchedules()
    }
  }, [shouldShowUsersMeFilters, fetchSchedules])

  // Check if there are more schedules to load
  const hasMoreSchedules =
    schedulesPagination &&
    schedulesPagination.current_page < schedulesPagination.total_pages

  // Handle schedules dropdown scroll for infinite scroll
  const handleSchedulesScroll = useCallback(
    (e) => {
      const target = e.target
      const nearBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight <
        ORGANIZATIONS_SCROLL_THRESHOLD
      if (nearBottom && hasMoreSchedules && !schedulesAppending) {
        fetchSchedules(
          schedulesSearchQueryRef.current,
          schedulesPagination.current_page + 1,
          true
        )
      }
    },
    [hasMoreSchedules, schedulesAppending, schedulesPagination, fetchSchedules]
  )

  // Handle schedules search with debounce
  const handleSchedulesSearch = useCallback(
    (value) => {
      if (schedulesSearchTimeoutRef.current) {
        clearTimeout(schedulesSearchTimeoutRef.current)
      }
      if (value) {
        schedulesSearchTimeoutRef.current = setTimeout(() => {
          schedulesSearchQueryRef.current = value
          fetchSchedules(value)
        }, DEBOUNCE_MS)
      }
    },
    [fetchSchedules]
  )

  // Get display value for schedules AutoComplete
  const getScheduleDisplayValue = useCallback(() => {
    if (!selectedSchedule) {
      return isCanlan ? 'All Seasons' : 'All Competitions'
    }
    return selectedSchedule.name_full || selectedSchedule.name
  }, [selectedSchedule])

  // Transform organizations tree data for TreeSelect
  const organizationsTreeData = useMemo(() => {
    if (!organizationsTree) {
      return []
    }

    const allOption = {
      value: ALL_VALUE,
      title: isCanlan ? 'All Leagues' : 'All Organizations',
      label: isCanlan ? 'All Leagues' : 'All Organizations',
      isLeaf: true,
    }

    return [allOption, ...transformToTreeSelectData(organizationsTree)]
  }, [organizationsTree])

  // Default expanded keys (first level)
  const defaultExpandedKeys = useMemo(() => {
    if (!organizationsTree) {
      return []
    }
    return getFirstLevelKeys(organizationsTree)
  }, [organizationsTree])

  // Custom tree node renderer with image
  const renderTreeNode = useCallback((nodeData) => {
    // "All" option doesn't have data - but still needs consistent padding
    if (!nodeData.data) {
      return (
        <div css={treeNodeStyle}>
          <span css={treeNodeNameStyle}>{nodeData.title}</span>
        </div>
      )
    }

    const handleImageError = (e) => {
      e.currentTarget.onerror = null
      e.currentTarget.src = ORG_PLACEHOLDER
    }

    return (
      <div css={treeNodeStyle}>
        <img
          src={nodeData.data.image_url || ORG_PLACEHOLDER}
          alt={nodeData.data.name}
          css={treeNodeImageStyle}
          onError={handleImageError}
          loading='lazy'
        />
        <div css={treeNodeTextStyle}>
          <span css={treeNodeNameStyle}>{nodeData.title}</span>
        </div>
      </div>
    )
  }, [])

  // Schedule options for AutoComplete
  const scheduleOptions = useMemo(
    () => [
      {
        label: isCanlan ? 'All Seasons' : 'All Competitions',
        value: 'all',
      },
      ...schedules.map((schedule) => {
        const displayName = schedule.name_full || schedule.name
        return {
          value: schedule.id,
          label: (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
              `}
            >
              <img
                src={schedule.image?.url || COMPETITION_PLACEHOLDER}
                alt={displayName}
                css={optionImageStyle}
                onError={(e) => {
                  e.currentTarget.onerror = null
                  e.currentTarget.src = COMPETITION_PLACEHOLDER
                }}
                loading='lazy'
              />
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 2px;
                  min-width: 0;
                  flex: 1;
                `}
              >
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.NEUTRAL_0}
                  style='line-height: 1;'
                >
                  {displayName}
                </Text>
                {schedule.organization?.name && (
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.NEUTRAL_50}
                    style='line-height: 1;'
                  >
                    {schedule.organization.name}
                  </Text>
                )}
              </div>
            </div>
          ),
        }
      }),
    ],
    [schedules]
  )

  const { groupedSportOptions, flatSportOptions } = useMemo(() => {
    const fullStatsOptions =
      sports
        ?.map((sport) => ({
          label: sport.name_full,
          value: sport.id,
          text: sport.name_full,
          is_generic: false,
        }))
        .filter((value) => value?.label !== 'Generic')
        .sort((a, b) => a.label.localeCompare(b.label)) || []

    const genericSportId = sports?.find((s) => s.name_full === 'Generic')?.id
    const basicStatsOptions = []

    if (genericSports) {
      genericSports.forEach((sport) => {
        if (!basicStatsOptions.find((s) => s.value === sport.id)) {
          basicStatsOptions.push({
            label: `${sport.name} (Basic)`,
            value: sport.id,
            text: `${sport.name} (Basic)`,
            is_generic: true,
            generic_sport_id: genericSportId,
          })
        }
      })
    }

    basicStatsOptions.sort((a, b) => a.label.localeCompare(b.label))

    const grouped = [
      {
        label: 'All Sports',
        value: ALL_VALUE,
      },
      {
        label: 'Full Stats',
        options: fullStatsOptions,
      },
      {
        label: 'Basic Stats',
        options: basicStatsOptions,
      },
    ]

    return {
      groupedSportOptions: grouped,
      flatSportOptions: [...fullStatsOptions, ...basicStatsOptions],
    }
  }, [sports, genericSports])

  // This function is used to filter the options in the select dropdown
  // when we group the options
  const filterOptionGroup = useCallback((s, optionOrGroup) => {
    const isGroup = Array.isArray(optionOrGroup.options)
    if (isGroup) {
      return false
    }
    return optionOrGroup.label.toLowerCase().includes(s.toLowerCase())
  }, [])

  useEffect(() => {
    updateSportFilterList(flatSportOptions)
  }, [flatSportOptions, updateSportFilterList])

  // Refresh when refreshKey changes
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false
      return
    }

    if (refreshKey !== undefined) {
      refresh()
    }
  }, [refreshKey, refresh])

  // Initialize local favorites when items change and we're in favorites view
  useEffect(() => {
    if (isFavorites && items) {
      const favoriteIds = new Set(items.map((item) => item.id))
      setLocalFavorites(favoriteIds)
    }
  }, [items, isFavorites])

  const handleFavoriteChange = useCallback(
    (itemId, newFavoriteState) => {
      if (isFavorites) {
        setLocalFavorites((prev) => {
          const newSet = new Set(prev)
          if (newFavoriteState) {
            newSet.add(itemId)
          } else {
            newSet.delete(itemId)
          }
          return newSet
        })
      }
    },
    [isFavorites]
  )

  const handleScroll = useCallback(() => {
    if (loading || isLoadingMoreRef.current) {
      return
    }

    const { scrollTop, scrollHeight, clientHeight } = document.documentElement
    const nearBottom =
      scrollHeight - (scrollTop + clientHeight) < SCROLL_THRESHOLD
    if (nearBottom && hasMore) {
      isLoadingMoreRef.current = true
      loadMore()
    }
  }, [hasMore, loading, loadMore])

  useEffect(() => {
    if (!loading) {
      isLoadingMoreRef.current = false
    }
  }, [loading])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  const columns = useMemo(() => {
    const cols = [
      {
        title: t('common:name'),
        key: specificNameColumn,
        dataIndex: specificNameColumn,
        sorter: true,
        ellipsis: true,
        showSorterTooltip: false,
        sortOrder:
          sort === specificNameColumn
            ? direction === 'asc'
              ? 'ascend'
              : 'descend'
            : null,
        render: (_, item) => (
          <Link
            to={{
              pathname: `${ROUTES.TEAM_ROOT}/${item.id}`,
              state: { fromParent: parentId },
            }}
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              width: 100%;
              color: ${colors.NEUTRAL_0};
            `}
          >
            <Avatar
              src={
                getImageThumbnailId(item) ||
                item?.image?.url ||
                '/images/generic_placeholders/placeholder_team.png'
              }
              icon={<Icon name='user-friends' fontSize={14} />}
              shape='square'
              css={css`
                background-color: white;
                flex-shrink: 0;
                width: 32px;
                height: 32px;
                background-color: ${colors.NEUTRAL_0};
                border-radius: 8px !important;
              `}
            />
            <div
              css={css`
                flex: 1;
                min-width: 0;
                overflow: hidden;
                display: flex;
                flex-direction: column;
              `}
            >
              <Text
                variant='paragraphSmall'
                weight='regular'
                color={colors.NEUTRAL_0}
              >
                {item?.name || item?.name_full}
              </Text>
              <div
                css={css`
                  display: flex;
                  gap: 2px;
                `}
              >
                {item?.roles && item?.roles?.length > 0 ? (
                  <Text
                    variant='paragraphXSmall'
                    color={colors.NEUTRAL_50}
                    weight='regular'
                    style={`
                      text-transform: capitalize;
                    `}
                  >
                    {Array.from(
                      new Set(item?.roles?.map((role) => role.role_name))
                    ).join(' | ')}
                  </Text>
                ) : null}
              </div>
            </div>
          </Link>
        ),
      },
    ]

    if (displayOrganizationColumn) {
      cols.push({
        title: 'Organization',
        key: 'organization.name',
        dataIndex: 'organization.name',
        sorter: false,
        ellipsis: true,
        showSorterTooltip: false,
        sortOrder: false,
        render: (_, item) =>
          item?.organization?.id ? (
            <Link
              to={{
                pathname: `${ROUTES.ORG_ROOT}/${item?.organization?.id}`,
                state: { fromParent: parentId },
              }}
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                width: 100%;
                color: ${colors.NEUTRAL_0};
              `}
            >
              {/* <Avatar
                src={
                  getImageThumbnailId(item?.organization) ||
                  item?.organization?.image?.url ||
                  '/images/generic_placeholders/placeholder_organization.png'
                }
                icon={<Icon name='building' fontSize={14} />}
                shape='square'
                css={css`
                  background-color: ${colors.NEUTRAL_0};
                  flex-shrink: 0;
                  width: 32px;
                  height: 32px;
                  border-radius: 8px !important;
                `}
              /> */}
              <div
                css={css`
                  flex: 1;
                  min-width: 0;
                  overflow: hidden;
                  display: flex;
                  flex-direction: column;
                `}
              >
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.NEUTRAL_0}
                  style={`
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                  `}
                >
                  {item?.organization?.name}
                </Text>
              </div>
            </Link>
          ) : null,
      })
    }

    cols.push(
      {
        title: 'Sport',
        key: 'sport_id',
        dataIndex: 'sport_id',
        sorter: false,
        ellipsis: true,
        showSorterTooltip: false,
        sortOrder: null,
        render: (_, item) => (
          <span>
            {item?.generic_sport_name
              ? `${item?.generic_sport_name} (Basic)`
              : sports?.find((s) => s.id === item?.sport_id)?.name_full}
          </span>
        ),
      },

      // {
      //   title: t('common:email'),
      //   key: 'email',
      //   dataIndex: 'email',
      //   sorter: false,
      //   ellipsis: true,
      //   showSorterTooltip: false,
      //   sortOrder: null,
      //   render: (email) =>
      //     email ? <a href={`mailto:${email}`}>{email}</a> : '',
      // },
      // {
      //   title: t('common:website'),
      //   key: 'website',
      //   dataIndex: 'website',
      //   sorter: false,
      //   ellipsis: true,
      //   showSorterTooltip: false,
      //   sortOrder: null,
      //   render: (website) => {
      //     if (!website) {
      //       return ''
      //     }
      //     const href = website.startsWith('http')
      //       ? website
      //       : `https://${website}`
      //     return (
      //       <a href={href} target='_blank' rel='noopener noreferrer'>
      //         {website}
      //       </a>
      //     )
      //   },
      // },
      // {
      //   title: t('common:phone'),
      //   key: 'phone',
      //   dataIndex: 'phone',
      //   sorter: false,
      //   ellipsis: true,
      //   showSorterTooltip: false,
      //   sortOrder: null,
      // },
      // {
      //   title: 'Support Email',
      //   key: 'support_email',
      //   dataIndex: 'support_email',
      //   sorter: false,
      //   ellipsis: true,
      //   showSorterTooltip: false,
      //   sortOrder: null,
      //   render: (email) =>
      //     email ? <a href={`mailto:${email}`}>{email}</a> : '',
      // },
      // {
      //   title: 'Support Website',
      //   key: 'support_website',
      //   dataIndex: 'support_website',
      //   sorter: false,
      //   ellipsis: true,
      //   showSorterTooltip: false,
      //   sortOrder: null,
      //   render: (website) => {
      //     if (!website) {
      //       return ''
      //     }
      //     const href = website.startsWith('http')
      //       ? website
      //       : `https://${website}`
      //     return (
      //       <a href={href} target='_blank' rel='noopener noreferrer'>
      //         {website}
      //       </a>
      //     )
      //   },
      // },
      // {
      //   title: 'Support Phone',
      //   key: 'support_phone',
      //   dataIndex: 'support_phone',
      //   sorter: false,
      //   ellipsis: true,
      //   showSorterTooltip: false,
      //   sortOrder: null,
      // },
      {
        // title: 'Options',
        title: '',
        align: 'right',
        key: 'actions',
        width: 150,
        render: (_, item) => {
          const permission = permissions[item.id] || {}

          const favoriteState = isFavorites
            ? localFavorites.has(item.id)
            : item.is_favorite

          const moreOptions = []

          if (permission?.update) {
            moreOptions.push({
              key: 'plugin',
              Component: renderGeneratePluginTag,
              componentProps: {
                Button: renderGeneratePluginTag,
                schedule: item,
                scheduleId: item.id,
                onClick: () => {
                  onClickWebPluginOnTeamList(item)
                },
              },
            })
          }

          if (permission?.delete) {
            moreOptions.push({
              key: 'delete',
              Component: OptionButtonDelete,
              componentProps: {
                useForceDelete: true,
                unmountOnClose: true,
                entityType: ENTITY_TYPES.team,
                itemId: item.id,
                title: `Delete ${item.name_full}`,
                type: t('common:team'),
                iconName: 'trash',
                onComplete: refresh,
              },
            })
          }

          return (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                justify-content: flex-end;
              `}
            >
              {permission.update && (
                <Tooltip title={t('Web:editTeam')} mouseEnterDelay={1}>
                  <OptionButtonIcon
                    name='pen'
                    onClick={() => setEditingTeam({ ...item, permission })}
                  />
                </Tooltip>
              )}
              <CustomOptionButtonFavourite
                entityType={ENTITY_TYPES.team}
                item={item}
                isFavourite={favoriteState}
                isFavoritesView={isFavorites}
                onFavoriteChange={(newState) =>
                  handleFavoriteChange(item.id, newState)
                }
              />
              {moreOptions.length > 0 && <MoreOptions options={moreOptions} />}
            </div>
          )
        },
      }
    )

    return cols
  }, [
    sort,
    direction,
    permissions,
    isFavorites,
    localFavorites,
    onClickWebPluginOnTeamList,
    handleFavoriteChange,
    sports,
    parentId,
    refresh,
    specificNameColumn,
    displayOrganizationColumn,
    setEditingTeam,
  ])

  return (
    <div
      css={css`
        width: 100%;
      `}
    >
      {/* Global styles for AutoComplete and TreeSelect dropdown */}
      {shouldShowUsersMeFilters && (
        <style>
          {`
            .team-list-autocomplete-dropdown {
              z-index: 999999 !important;
              background-color: ${colors.SECONDARY_500} !important;
              padding-top: 4px;
              padding-left: 0;
              padding-right: 0;
              padding-bottom: 0;
            }
            .team-list-autocomplete-dropdown .ant-select-item-option-content {
              font-size: 14px !important;
              font-weight: 400 !important;
              color: ${colors.WHITE} !important;
            }
            .team-list-autocomplete-dropdown .ant-select-tree {
              background: ${colors.SECONDARY_500} !important;
              color: ${colors.WHITE} !important;
            }
            .team-list-autocomplete-dropdown .ant-select-tree-node-content-wrapper {
              color: ${colors.WHITE} !important;
            }
            .team-list-autocomplete-dropdown .ant-select-tree-node-content-wrapper:hover {
              background: ${colors.SECONDARY_400} !important;
            }
            .team-list-autocomplete-dropdown .ant-select-tree-treenode-selected .ant-select-tree-node-content-wrapper {
              background: ${colors.SECONDARY_400} !important;
            }
            .team-list-autocomplete-dropdown .ant-select-tree-switcher {
              color: ${colors.NEUTRAL_50} !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
            }
            .team-list-autocomplete-dropdown .ant-select-tree-indent-unit {
              width: 12px !important;
            }
          `}
        </style>
      )}
      <div
        css={css`
          margin-top: 16px;
          margin-bottom: 16px;
          display: flex;
          justify-content: flex-start;
          flex-wrap: wrap;
          gap: 16px;
          align-items: flex-end;
          @media (max-width: 768px) {
            padding: 0 16px;
            flex-direction: column;
          }
        `}
      >
        <div
          css={css`
            display: flex;
            gap: 16px;
            flex-wrap: wrap;
            align-items: center;
            @media (max-width: 768px) {
              width: 100%;
              flex-direction: column;
            }
          `}
        >
          {isMembership && (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 8px;
                align-self: flex-start;
                flex-shrink: 1;
                @media (max-width: 768px) {
                  width: 100%;
                }
              `}
            >
              <MembershipTypeInfoLabel />
              <MembershipTypeFilter
                selected={membershipType}
                onSelect={(value) => {
                  handleMembershipType(value)
                  // Only call legacy callback if new controlled callback is not provided
                  if (!onMembershipTypeChangeExternal) {
                    onMembershipTypeChange?.(value)
                  }
                }}
              />
            </div>
          )}
          <div
            css={css`
              flex: 1;
              display: flex;
              flex-direction: column;
              gap: 8px;
              @media (max-width: 768px) {
                width: 100%;
                flex-direction: column;
              }
            `}
          >
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.NEUTRAL_0}
            >
              Search
            </Text>
            <Input
              className='list-search-input'
              placeholder={t('common:search')}
              prefix={
                <SearchOutlined
                  css={css`
                    font-size: 16px;
                    color: ${colors.NEUTRAL_50};
                  `}
                />
              }
              suffix={
                loading && items.length > 0 ? <Spin size='small' /> : null
              }
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              allowClear={true}
              css={css`
                ${paragraphSmall}
                ${weightRegular}
              width: 300px;
                color: ${colors.WHITE};
                border-radius: 8px;
                background: transparent;
                height: 40px !important;
                .ant-input {
                  background-color: transparent;
                  color: ${colors.WHITE};
                }
                .ant-input-prefix {
                  color: ${colors.NEUTRAL_100};
                }
                &::placeholder {
                  color: ${colors.NEUTRAL_100};
                }
                @media (max-width: 1023px) {
                  width: 100%;
                }
                &:hover {
                  background-color: transparent;
                }
              `}
            />
          </div>
          {shouldShowUsersMeFilters && (
            <div
              css={css`
                flex: 1;
                display: flex;
                flex-direction: column;
                gap: 8px;
                @media (max-width: 1023px) {
                  width: 100%;
                  flex-direction: column;
                }
              `}
            >
              <Text
                variant='paragraphSmall'
                weight='regular'
                color={colors.NEUTRAL_0}
              >
                {isCanlan ? 'Leagues' : 'Organizations'}
              </Text>
              <TreeSelect
                css={filterTreeSelect}
                popupClassName='team-list-autocomplete-dropdown'
                labelInValue
                value={
                  selectedOrganization
                    ? {
                        value: selectedOrganization.id,
                        label:
                          selectedOrganization.name_full ||
                          selectedOrganization.name,
                      }
                    : {
                        value: ALL_VALUE,
                        label: isCanlan ? 'All Leagues' : 'All Organizations',
                      }
                }
                placeholder={isCanlan ? 'All Leagues' : 'All Organizations'}
                treeData={organizationsTreeData}
                showSearch
                filterTreeNode={(inputValue, treeNode) => {
                  const searchText = treeNode.title?.toLowerCase() || ''
                  return searchText.includes(inputValue.toLowerCase())
                }}
                onChange={(valueObj) => {
                  const value = valueObj?.value
                  if (value === ALL_VALUE) {
                    setSelectedOrganization(null)
                    handleOrganizationIdChange('')
                  } else if (organizationsTree) {
                    const node = findNodeById(organizationsTree, value)
                    if (node) {
                      setSelectedOrganization(treeNodeToOrganization(node))
                      handleOrganizationIdChange(value)
                    }
                  }
                }}
                allowClear={false}
                onClear={() => {
                  setSelectedOrganization(null)
                  handleOrganizationIdChange('')
                }}
                disabled={organizationsLoading}
                treeDefaultExpandedKeys={defaultExpandedKeys}
                treeTitleRender={renderTreeNode}
                popupMatchSelectWidth={isMobile ? true : false}
                dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                notFoundContent={
                  organizationsLoading ? <Spin size='small' /> : null
                }
                open={organizationsDropdownOpen}
                onDropdownVisibleChange={setOrganizationsDropdownOpen}
                suffixIcon={
                  <DownOutlined
                    style={{ color: colors.NEUTRAL_50, cursor: 'pointer' }}
                    onClick={() =>
                      setOrganizationsDropdownOpen((prev) => !prev)
                    }
                  />
                }
              />
            </div>
          )}
          {shouldShowUsersMeFilters && (
            <div
              css={css`
                flex: 1;
                display: flex;
                flex-direction: column;
                gap: 8px;
                @media (max-width: 768px) {
                  width: 100%;
                  flex-direction: column;
                  align-self: flex-start;
                }
              `}
            >
              <Text
                variant='paragraphSmall'
                weight='regular'
                color={colors.NEUTRAL_0}
              >
                {isCanlan ? 'Seasons' : 'Competitions'}
              </Text>
              <AutoComplete
                popupMatchSelectWidth={false}
                css={filterAutoCompleteStyle}
                popupClassName='team-list-autocomplete-dropdown'
                value={getScheduleDisplayValue()}
                placeholder={isCanlan ? 'All Seasons' : 'All Competitions'}
                options={scheduleOptions}
                onSearch={handleSchedulesSearch}
                onSelect={(value) => {
                  if (value === 'all') {
                    setSelectedSchedule(null)
                    handleScheduleIdChange('')
                    schedulesSearchQueryRef.current = ''
                    fetchSchedules('')
                  } else {
                    const schedule = schedules.find((s) => s.id === value)
                    setSelectedSchedule(schedule || null)
                    handleScheduleIdChange(value)
                  }
                }}
                onPopupScroll={handleSchedulesScroll}
                filterOption={false}
                allowClear={false}
                onClear={() => {
                  setSelectedSchedule(null)
                  handleScheduleIdChange('')
                  schedulesSearchQueryRef.current = ''
                  fetchSchedules('')
                }}
                disabled={schedulesLoading}
                open={schedulesDropdownOpen}
                onDropdownVisibleChange={setSchedulesDropdownOpen}
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    {schedulesAppending && (
                      <div
                        css={css`
                          display: flex;
                          justify-content: center;
                          padding: 8px;
                        `}
                      >
                        <Spin size='small' />
                      </div>
                    )}
                  </>
                )}
                suffixIcon={
                  <DownOutlined
                    style={{ color: colors.NEUTRAL_50, cursor: 'pointer' }}
                    onClick={() => setSchedulesDropdownOpen((prev) => !prev)}
                  />
                }
              />
            </div>
          )}

          <div
            css={css`
              flex: 1;
              display: flex;
              flex-direction: column;
              gap: 8px;
              gap: 8px;
              @media (max-width: 768px) {
                width: 100%;
                flex-direction: column;
                align-self: flex-start;
              }
            `}
          >
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.NEUTRAL_0}
            >
              Sport
            </Text>
            <Select
              placeholder='All Sports'
              value={sportFilter || ALL_VALUE}
              onChange={(value) => {
                if (value === ALL_VALUE) {
                  handleSportFilter(undefined, flatSportOptions)
                } else {
                  handleSportFilter(value, flatSportOptions)
                }
              }}
              options={groupedSportOptions}
              css={css`
                width: 200px;
                height: 40px;
                .ant-select-selector {
                  background-color: transparent !important;
                  color: ${colors.WHITE} !important;
                  border-radius: 8px !important;
                  height: 40px !important;
                  font-size: 14px !important;
                }
                .ant-select-arrow {
                  color: ${colors.NEUTRAL_100};
                }
                .ant-select-clear {
                  background: transparent;
                  color: ${colors.NEUTRAL_100};
                }
                @media (max-width: 1023px) {
                  width: 100%;
                }
              `}
              filterOption={filterOptionGroup}
              showSearch={true}
            />
          </div>
        </div>
        {showTotalCount && totalCount > 0 && !loading && (
          <div
            css={css`
              display: flex;
              align-items: center;
              height: 40px;
              align-self: flex-end;
            `}
          >
            <Text
              variant='paragraphSmall'
              weight='medium'
              color={colors.NEUTRAL_50}
            >
              {totalCount.toLocaleString()}{' '}
              {totalCount === 1 ? 'Team' : 'Teams'}
            </Text>
          </div>
        )}
      </div>
      {error ? (
        <Alert
          message={error}
          type='error'
          showIcon
          css={css`
            margin-bottom: 16px;
            margin-top: 16px;
          `}
        />
      ) : null}
      {loading && items.length === 0 ? (
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 200px;
          `}
        >
          <Spin
            size='small'
            css={css`
              .ant-spin-dot-item {
                background-color: ${colors.PRIMARY};
              }
            `}
          />
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={items}
          rowKey='id'
          pagination={false}
          onChange={(pagination, filters, sorter) => {
            if (sorter.order) {
              if (
                sorter.field !== specificNameColumn &&
                sorter.order === 'ascend'
              ) {
                handleSort(sorter.field, 'desc')
              } else {
                handleSort(
                  sorter.field,
                  sorter.order === 'ascend' ? 'asc' : 'desc'
                )
              }
            } else {
              handleSort(specificNameColumn, 'asc')
            }
          }}
          size='small'
          scroll={{ x: isMobile || width < 1200 ? 1000 : undefined }}
          css={getTableStyles(isMobile)}
        />
      )}
      {loading && items.length > 0 && (
        <div
          css={css`
            display: flex;
            justify-content: center;
            padding: 20px;
          `}
        >
          <Spin size='small' color={colors.PRIMARY} />
        </div>
      )}
      <FloatButton.BackTop
        icon={<ArrowUpOutlined />}
        css={css`
          .ant-float-btn-body {
            background-color: ${colors.PRIMARY} !important;
          }
          .ant-float-btn-icon {
            color: ${colors.NEUTRAL_0} !important;
          }
        `}
      />
      <TeamModal
        isOpen={!!editingTeam}
        onClose={() => setEditingTeam(null)}
        initialState={
          editingTeam
            ? parsers[ENTITY_TYPES.team]({
                ...editingTeam,
                permission: editingTeam.permission,
              })
            : undefined
        }
        organizationId={editingTeam?.organization?.id}
        onSuccess={() => {
          setEditingTeam(null)
          refresh()
        }}
        permission={editingTeam?.permission}
        shouldAllowEditTeam={true}
      />
    </div>
  )
}

const mapStateToProps = (state) => {
  const genericSports = getAllGenericSportProperties(state)
  return {
    genericSports,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {}
}

export default connect(mapStateToProps, mapDispatchToProps)(TeamListTable)
